import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CheckCircle2, ArrowRight, X, Activity, ShieldCheck, Music, BookCheck } from "lucide-react";

const STEPS = [
  {
    icon: Activity,
    title: "1. Take the assessment",
    body: "24 questions across Metadata, Royalty Infrastructure, Rights, Catalog, Business. Generates your 0–1000 BeatCredsi Score.",
    cta: "Start assessment",
    to: "/assessment",
    check: (u, a) => !!a,
  },
  {
    icon: ShieldCheck,
    title: "2. Claim your Passport",
    body: "Choose your @username — your permanent beatcredsi.com/c/{name} URL. Share it in your IG bio, EPK, email signature.",
    cta: "Pick a username",
    to: "/dashboard#passport",
    check: (u) => !!u?.username,
  },
  {
    icon: Music,
    title: "3. Add your credits",
    body: "Paste links to Muso.AI, Genius, AllMusic where your producer credits are listed. Visitors verify your work on the source.",
    cta: "Add credit links",
    to: "/dashboard#discog",
    check: (u) => {
      const d = u?.discography_links || {};
      return !!(d.muso_ai_url || d.genius_url || d.allmusic_url);
    },
  },
  {
    icon: BookCheck,
    title: "4. Upload Evidence",
    body: "Upload registration PDFs, copyright certificates, or PRO screenshots — admin reviews and stamps your Passport.",
    cta: "Upload evidence",
    to: "/royai#evidence",
    check: (u) => !!u?.identity_verified,
  },
];

const ONBOARDING_KEY = "bc_onboarding_dismissed_v1";

export default function OnboardingTour({ assessment }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    const wasDismissed = localStorage.getItem(ONBOARDING_KEY) === "1";
    setDismissed(wasDismissed);
    // Auto-open on first visit if not already dismissed AND user hasn't completed all 4 steps
    const completedAll = STEPS.every((s) => s.check(user, assessment));
    if (!wasDismissed && !completedAll) setOpen(true);
  }, [user, assessment]);

  const close = (permanently = false) => {
    setOpen(false);
    if (permanently) {
      localStorage.setItem(ONBOARDING_KEY, "1");
      setDismissed(true);
    }
  };

  if (!user) return null;

  const completedCount = STEPS.filter((s) => s.check(user, assessment)).length;
  const progressPct = (completedCount / STEPS.length) * 100;

  if (!open) {
    // Re-open button for users who dismissed but want to revisit
    if (dismissed || completedCount === STEPS.length) return null;
    return (
      <button onClick={() => setOpen(true)} data-testid="onboarding-reopen-btn"
        className="fixed bottom-6 right-6 z-40 px-4 py-3 bg-amber-500 text-black font-semibold text-xs uppercase tracking-widest hover:brightness-110 shadow-2xl flex items-center gap-2">
        Get Started · {completedCount}/{STEPS.length}
        <ArrowRight size={12} />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="w-full max-w-2xl bg-[#0c0c0c] border border-amber-500/20">
        <div className="flex items-center justify-between px-7 py-5 border-b border-white/5">
          <div>
            <div className="text-overline">Get Started</div>
            <h2 className="font-display text-2xl font-medium mt-0.5">Welcome to BeatCredsi<span className="text-amber-500 text-base align-super">™</span></h2>
          </div>
          <button onClick={() => close(false)} data-testid="onboarding-close-btn"
            className="p-2 border border-white/15 text-neutral-400 hover:border-white/30 hover:text-white">
            <X size={14} />
          </button>
        </div>

        <div className="px-7 pt-5">
          <div className="flex items-center justify-between mb-2 text-xs font-mono-tech uppercase tracking-widest">
            <span className="text-neutral-500">Progress</span>
            <span className="text-amber-500">{completedCount} of {STEPS.length}</span>
          </div>
          <div className="h-1 bg-white/5 mb-6">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <div className="px-7 pb-7 space-y-2 max-h-[55vh] overflow-y-auto">
          {STEPS.map((s, i) => {
            const done = s.check(user, assessment);
            const Icon = s.icon;
            return (
              <div key={i}
                data-testid={`onboarding-step-${i + 1}`}
                className={`p-4 border ${done ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 hover:border-amber-500/40"} transition-colors`}>
                <div className="flex items-start gap-4">
                  <div className={`p-2 shrink-0 ${done ? "bg-emerald-500/15" : "bg-amber-500/10"}`}>
                    {done ? <CheckCircle2 size={16} className="text-emerald-400" /> : <Icon size={16} className="text-amber-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white mb-1">{s.title}</div>
                    <div className="text-xs text-neutral-400 leading-relaxed mb-3">{s.body}</div>
                    {!done && (
                      <Link to={s.to} onClick={() => close(false)}
                        data-testid={`onboarding-cta-${i + 1}`}
                        className="inline-flex items-center gap-1.5 text-xs text-amber-500 hover:underline font-mono-tech uppercase tracking-widest">
                        {s.cta} <ArrowRight size={12} />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-7 py-4 border-t border-white/5 flex justify-between items-center">
          <button onClick={() => close(true)} data-testid="onboarding-skip-btn"
            className="text-xs text-neutral-500 hover:text-neutral-300 font-mono-tech uppercase tracking-widest">
            Don&apos;t show again
          </button>
          <button onClick={() => close(false)}
            className="text-xs text-amber-500 hover:underline font-mono-tech uppercase tracking-widest">
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}
